import { light } from '@fortawesome/fontawesome-svg-core/import.macro'
import { AnimatePresence, motion } from 'framer-motion'
import { useRef, useState } from 'react'
import {
  CustomDateTimeRangeModal,
  Icon,
  RangeSummaryLabel,
  Text,
  useTimePickerClickOutside,
} from 'src/components/ui'
import { TimeRange } from 'src/types'
import { ExcludedPeriodLabel } from './ExcludedPeriodLabel'
import type { ExcludedPeriod } from 'pages/site/models'

type Props = {
  period: ExcludedPeriod
  isEdit: boolean
  onTimeRangeChange: (value: TimeRange) => void
  onHiddenChange: (value: boolean) => void
  remove: () => void
}

export function ExcludedPeriod({
  period,
  isEdit,
  onTimeRangeChange,
  onHiddenChange,
  remove,
}: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  useTimePickerClickOutside(ref, isOpen, setIsOpen)

  if (!isEdit) {
    return <ExcludedPeriodLabel period={period} />
  }

  return (
    <div className="flex w-full items-center gap-xs">
      <div
        ref={ref}
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex h-l w-full cursor-pointer items-center justify-between rounded-2xs border border-solid border-border bg-background p-xs"
      >
        <div className="flex items-center gap-xs">
          <Icon icon={light('calendar-range')} />

          {!period.timeRange ? (
            <Text variant="description" className="pt-3xs opacity-60">
              Date...
            </Text>
          ) : (
            <RangeSummaryLabel value={period.timeRange} options={[]} />
          )}
        </div>
        <Icon icon={light('chevron-down')} />
        <AnimatePresence>
          {isOpen && (
            <motion.div
              onClick={e => e.stopPropagation()}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.1 }}
              className="absolute left-0 top-[calc(100%+8px)] z-50 rounded-2xs border border-solid border-border bg-background p-xs shadow-dropdown"
            >
              <CustomDateTimeRangeModal
                maxDate={new Date()}
                border={false}
                timeRange={period.timeRange}
                onChange={onTimeRangeChange}
                onClose={() => setIsOpen(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Icon
        icon={period.hidden ? light('eye-slash') : light('eye')}
        className="w-s cursor-pointer"
        onClick={() => onHiddenChange(!period.hidden)}
      />
      <Icon icon={light('trash')} className="cursor-pointer" onClick={remove} />
    </div>
  )
}
